/**
 * 后端 API 客户端（阶段 3）。
 *
 * 替代旧 services/index.ts 里直连第三方数据源的做法，
 * 统一走自己的 /api/* 后端。前端只调本模块。
 *
 * 鉴权：API Key 存 localStorage，每次请求带 X-API-Key 头。
 * camelCase：后端已转好，前端直接用。
 */

const API_KEY_STORAGE = "guxun.api-key";

/**
 * API 基址。
 * - 开发期：空串 → 同源 /api/*（vite 代理到本地 dev_server）
 * - 生产期：构建时通过 VITE_API_BASE 注入函数 URL（如 https://xxx.app.tcloudbase.com）
 *   → 请求变成绝对地址，无需 COS 回源。
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "";

/** 读取本地存的 API Key */
export function getApiKey(): string {
  return localStorage.getItem(API_KEY_STORAGE) || "";
}

/** 设置 API Key（控制面板首次进入时填） */
export function setApiKey(key: string): void {
  localStorage.setItem(API_KEY_STORAGE, key);
}

export function hasApiKey(): boolean {
  return !!getApiKey();
}

/** 是否配置了后端 API Key（决定是否显示配置引导） */
export function clearApiKey(): void {
  localStorage.removeItem(API_KEY_STORAGE);
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const key = getApiKey();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (key) headers["X-API-Key"] = key;

  const resp = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // 非 JSON 或网络错误
  let payload: any = null;
  try {
    payload = await resp.json();
  } catch {
    throw new ApiError(resp.status, `响应解析失败 (HTTP ${resp.status})`);
  }

  if (!resp.ok || payload.ok === false) {
    throw new ApiError(resp.status, payload.error || `请求失败 (HTTP ${resp.status})`);
  }
  return payload.data as T;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

// ============================================================
// 类型（与后端 camelCase 对齐）
// ============================================================

export interface GlobalSettings {
  pushEnabled: boolean;
  updatedAt: string;
}

export interface SubscriptionDimensions {
  quote: boolean;
  sector: boolean;
  news: boolean;
  international: boolean;
  announce: boolean;
}

export interface Subscription {
  stockCode: string;
  stockName: string;
  mktNum: string;
  enabled: boolean;
  lastPushedAt: string;
  dimensions: SubscriptionDimensions;
  minImpact: string;
  maxPerPush: number;
  pushplusToken: string;
}

export interface PushHistoryItem {
  time: string;
  stockCode: string;
  stockName: string;
  newsCount: number;
  channel: string;
  success: boolean;
  title: string;
  content: string;
}

export interface PushStats {
  total: number;
  pushed: number;
  skipped: number;
  failed: number;
  stocksWithNews?: number;
  mode?: string;
  details: Array<{ stock: string; ok: boolean; msg: string; count: number }>;
}

export interface HealthStatus {
  status: string;
  storageMode: string;
  deepseekEnabled: boolean;
  finnhubEnabled: boolean;
  dryRun: boolean;
  mergePushDefault?: boolean;
}

/** /api/news/preview 返回的新闻条目 */
export interface PreviewNewsItem {
  title: string;
  summary: string;
  url: string;
  source: string;
  sourceType: string;
  lang: string;
  publishedAt: string;
  credibility: string;
  translation: string;
  tendency: string; // bullish | bearish | neutral
  impact: string;   // high | medium | low
  confidence: number;
  logic: string;
  catalyst: string;
}

/** /api/news/preview 返回的预览结果 */
export interface NewsPreviewResult {
  ok: boolean;
  message: string;
  stock: {
    code: string;
    name: string;
    price: number;
    change: number;
    changePct: number;
    volume: number;
    amount: number;
    turnoverRate: number;
    netFlow: number;
    sector: string;
    market: string;
    currency: string;
  } | null;
  sectors: string[];
  newsCount: number;
  news: PreviewNewsItem[];
}

// ============================================================
// API 模块
// ============================================================

export const SettingsApi = {
  get: () => apiFetch<GlobalSettings>("/api/settings"),
  update: (pushEnabled: boolean) =>
    apiFetch<GlobalSettings>("/api/settings", {
      method: "PATCH",
      body: JSON.stringify({ pushEnabled }),
    }),
};

export const SubscriptionsApi = {
  list: () => apiFetch<Subscription[]>("/api/subscriptions"),
  create: (stockCode: string, stockName: string, mktNum: string) =>
    apiFetch<Subscription>("/api/subscriptions", {
      method: "POST",
      body: JSON.stringify({ stockCode, stockName, mktNum }),
    }),
  update: (code: string, patch: Partial<Subscription>) =>
    apiFetch<Subscription>(`/api/subscriptions/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  remove: (code: string) =>
    apiFetch<{ deleted: string }>(`/api/subscriptions/${encodeURIComponent(code)}`, {
      method: "DELETE",
    }),
};

export const PushApi = {
  history: (days = 7) =>
    apiFetch<PushHistoryItem[]>(`/api/push/history?days=${days}`),
  trigger: (force = true, merge = true) =>
    apiFetch<PushStats>("/api/push/trigger", {
      method: "POST",
      body: JSON.stringify({ force, merge }),
    }),
};

export const HealthApi = {
  check: () => apiFetch<HealthStatus>("/api/health"),
};

export const NewsApi = {
  /** 只读预览某只股票的增量新闻（不写库、不推送） */
  preview: (code: string, name: string, mkt: string, limit = 10) => {
    const qs = new URLSearchParams({ code, name, mkt, limit: String(limit) });
    return apiFetch<NewsPreviewResult>(`/api/news/preview?${qs.toString()}`);
  },
};

// ============================================================
// 股票搜索 + 实时行情（阶段4：迁移到后端，替代旧 services/index.ts 直连方案）
// ============================================================

/** /api/search 返回的搜索结果条目（轻量，无行情） */
export interface StockSearchResult {
  stockCode: string;
  stockName: string;
  mktNum: string;
  market: string;
  currency: string;
}

/** /api/quote 返回的实时行情（与后端 _quote_to_camel 对齐） */
export interface StockQuoteResult {
  code: string;
  name: string;
  price: number;
  change: number;
  changePct: number;
  volume: number;
  amount: number;
  turnoverRate: number;
  netFlow: number;
  sector: string;
  market: string;
  currency: string;
}

/** /api/screen 返回的选股结果条目（与后端 screen_stocks 输出对齐） */
export interface ScreenResult {
  code: string;
  name: string;
  mkt: string;
  price: number;
  preClose: number;
  change: number;
  changePct: number;
  high: number;
  low: number;
  turnover: number;
  pe: number;
  pb: number;
  amplitude: number;
  mktCap: number;       // 总市值（亿元）
  circMktCap: number;
  verified: "ok" | "warn";
  eps?: number;         // 每股收益（TTM）
  roe?: number;         // 净资产收益率 %
  reportDate?: string;  // 财报期（如 2026-03-31）
  industryName?: string;   // 所属行业（同花顺）
  industryRank?: number;   // 行业内市值排名（1=行业龙头）
  // 技术指标（仅当 techApplied 时存在）
  ma20?: number;
  ma60?: number;
  ma120?: number;
  priceLevel?: "high" | "mid" | "low";
  pctRank?: number;     // 价格分位 0-100
  drawdown?: number;    // 距 120 日高点回撤 %
}

export interface ScreenOptions {
  industries: { code: string; name: string }[];
  concepts: { code: string; name: string }[];
}

export interface ScreenParams {
  market?: string;      // shzb/szb/cyb/kcb
  board?: string[];     // 行业板块代码（多选）
  concept?: string[];   // 概念板块代码（多选）
  peMin?: number;
  peMax?: number;
  chgMin?: number;
  chgMax?: number;
  priceLevel?: string;  // high/mid/low
  ma?: string;          // above_ma20/below_ma20/...
  excludeSt?: boolean;
  epsMin?: number;
  epsMax?: number;
  roeMin?: number;
  roeMax?: number;
  hot?: string;         // week/month 近期热点
  leaderOnly?: boolean; // 仅行业龙头（行业内市值第1）
  limit?: number;
}

export interface ScreenResponse {
  total: number;
  results: ScreenResult[];
  techApplied: boolean;
}

export const StockApi = {
  /** 全球股票搜索（代码/名称/拼音模糊匹配，覆盖 A股/港股/美股/指数/ETF） */
  search: (q: string, limit = 20) => {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    return apiFetch<StockSearchResult[]>(`/api/search?${qs.toString()}`);
  },
  /** 单只股票实时行情（复用后端 collectors.get_quote，新浪数据源） */
  quote: (code: string, mkt: string, name = "") => {
    const qs = new URLSearchParams({ code, mkt, name });
    return apiFetch<StockQuoteResult>(`/api/quote?${qs.toString()}`);
  },
  /** 批量实时行情（一次请求拿多只，总览页用，降低偶发失败） */
  quotes: (items: { code: string; mkt: string; name?: string }[]) => {
    const segs = items.map((i) => `${i.code}:${i.mkt}:${i.name ?? ""}`);
    const qs = new URLSearchParams({ items: segs.join(",") });
    return apiFetch<{ quotes: StockQuoteResult[]; failed: string[] }>(
      `/api/quotes?${qs.toString()}`,
    );
  },
  /** A 股多条件选股（腾讯 qt 主源 + 新浪验证 + 腾讯 K 线技术指标） */
  screen: (params: ScreenParams) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    return apiFetch<ScreenResponse>(`/api/screen?${qs.toString()}`);
  },
  /** 选股可选项：行业板块 + 概念板块清单 */
  screenOptions: () => apiFetch<ScreenOptions>("/api/screen/options"),
};

/** /api/index-quote 主要指数条目（顶部跑马灯） */
export interface IndexQuoteItem {
  code: string;
  name: string;
  price: number;
  changePct: number;
}

export const IndexApi = {
  /** 主要指数实时行情（上证/深成/创业板/科创50/沪深300/恒指/道指/纳指） */
  quote: () => apiFetch<IndexQuoteItem[]>("/api/index-quote"),
};
